import { cn } from '@/lib/utils/cn'

const SIZES = { xs: 'size-[24px] text-caption', sm: 'size-[32px] text-caption',
                md: 'size-[40px] text-compact', lg: 'size-14 text-subtitle' } as const

export type AvatarSize = keyof typeof SIZES

/** Deterministic colour from the name, so one person is always the same colour. */
const PALETTE = [
  'bg-action-subtle text-action', 'bg-warning-subtle text-warning',
  'bg-cold-subtle text-cold', 'bg-qualified-subtle text-qualified',
  'bg-success-subtle text-success', 'bg-info-subtle text-info',
  'bg-hot-subtle text-hot', 'bg-warm-subtle text-warm',
]

function colourFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0
  return PALETTE[Math.abs(hash) % PALETTE.length]!
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

export type Presence = 'online' | 'away' | 'busy' | 'offline'

const PRESENCE_TONE: Record<Presence, string> = {
  online: 'bg-success',
  away: 'bg-warning',
  busy: 'bg-danger',
  offline: 'bg-text-tertiary',
}

export function Avatar({
  name,
  src,
  size = 'md',
  presence,
  className,
}: {
  name: string
  src?: string | null | undefined
  size?: AvatarSize
  presence?: Presence | undefined
  className?: string | undefined
}) {
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {src ? (
        <img
          src={src}
          alt=""
          width={40}
          height={40}
          className={cn(SIZES[size], 'rounded-pill object-cover')}
        />
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            SIZES[size],
            colourFor(name),
            'grid place-items-center rounded-pill font-semibold',
          )}
        >
          {initials(name)}
        </span>
      )}

      {presence && (
        <>
          <span
            aria-hidden="true"
            className={cn(
              'absolute right-0 bottom-0 size-2.5 rounded-pill',
              'ring-2 ring-surface',
              PRESENCE_TONE[presence],
            )}
          />
          {/* Presence is meaning, not decoration — it must reach a screen reader. */}
          <span className="visually-hidden">{presence}</span>
        </>
      )}
    </span>
  )
}

/** Stacked avatars with an overflow count. */
export function AvatarGroup({
  people,
  max = 4,
  size = 'sm',
}: {
  people: { name: string; src?: string | null | undefined }[]
  max?: number
  size?: AvatarSize
}) {
  const shown = people.slice(0, max)
  const overflow = people.length - shown.length

  return (
    <span className="flex items-center -space-x-2">
      {shown.map((person) => (
        <Avatar
          key={person.name}
          name={person.name}
          src={person.src}
          size={size}
          className="ring-2 ring-surface rounded-pill"
        />
      ))}
      {overflow > 0 && (
        <span
          className={cn(
            SIZES[size],
            'grid place-items-center rounded-pill ring-2 ring-surface',
            'bg-surface-hover text-text-secondary font-medium',
          )}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
}
